/**
 * Fondation feature flag gating the supervisor mode of the Présences sheet (#1164, which replaced
 * the « Salle d'examen » page of #975): whoever supervises a sitting records the attendance of the
 * students sitting an exam that day, WITHOUT any access to their files, through the two
 * `/api/ExamSupervision/…` routes. Toggled by an administrator at `/core/features`; the
 * declaration lives in `backend/Parcours.Core/Features/ExamSupervisionFeature.cs`, and this key
 * must match its `KEY` exactly — a key the catalog does not know reads as `false`, which HIDES
 * the mode.
 *
 * Declared `DefaultEnabled = false` on the backend: the teacher keeps full entry rights on the
 * Présences sheet, so the supervisor mode is an ADDITIONAL, narrow path a centre opts into. It is
 * therefore read strictly (`=== true`), never through `isEnabledUnlessTurnedOff`.
 *
 * Neither the menu entry nor the route carries the flag: teachers use the same Présences page with
 * the flag off. With the flag off, a supervisor-only account still reaches the page and reads
 * « Fonctionnalité désactivée » there, never an empty sheet — see {@link examSupervisionAccess}.
 */
export const EXAM_SUPERVISION_FEATURE = 'ExamSupervision';

/**
 * `LearningModulesModule.PERMISSION_EXAM_SUPERVISION` (« Surveillance d'examen »), carried by the
 * seeded « Surveillant d'examen » role. On the server it opens the two supervision routes and the
 * presence status catalogue, nothing else: no student file, no context-toolbar read.
 */
export const PERMISSION_EXAM_SUPERVISION = 'Parcours.LearningModule.SurveillanceExamen';

/**
 * The permissions the teacher sheet's own routes admit — `GET /api/Teachers/{id}/DaySheet`,
 * `GET /api/LearningPrograms/{id}/DaySheet` and `POST /api/Presences/Bulk`
 * (LearningModulesModule.cs). An account holding any of them uses the ordinary sheet; « Enseignement
 * élargi » alone opens neither route, so it does not count.
 */
export const PRESENCE_SHEET_PERMISSIONS: readonly string[] = [
  'Parcours.LearningModule.Administrateur',
  'Parcours.LearningModule.Superviseur',
  'Parcours.LearningModule.Enseignant',
];

/**
 * True when the exam supervision is the account's ONLY way to record presences: it holds
 * {@link PERMISSION_EXAM_SUPERVISION} and none of {@link PRESENCE_SHEET_PERMISSIONS}. Only then is
 * Présences locked into the supervisor mode (decided on 2026-09-28) — a teacher who also
 * supervises keeps the ordinary screen. False while the signed-in user is not known yet.
 */
export function isExamSupervisorOnly(permissions: readonly string[] | null | undefined): boolean {
  if (!permissions?.includes(PERMISSION_EXAM_SUPERVISION)) return false;
  return !PRESENCE_SHEET_PERMISSIONS.some(p => permissions.includes(p));
}

/**
 * What Présences may show an account, as far as the supervisor mode goes:
 *
 * - `none` — not a supervisor-only account: the ordinary sheet, whatever the flag says. Also a
 *   boot that is OVER with no signed-in user, which then never comes: a token session
 *   (`?apiToken=`, Fondation's TokenLayout) loads none, and it read the ordinary page before #1164;
 * - `pending` — the boot is still running and the signed-in user is not known yet, or a
 *   supervisor's flags have not landed: the flag map is EMPTY until the boot-time load resolves,
 *   and reading it then would flash « Fonctionnalité désactivée » on every cold load of an
 *   instance where the mode is on;
 * - `unavailable` — the boot is OVER with the map still empty: the flag load failed (the SDK logs
 *   it and carries on), so waiting would spin forever; the page offers to load the flags again;
 * - `disabled` — the flags are in and the supervision is not on (a key the catalog lacks is off:
 *   the catalog carries every declared key);
 * - `granted` — the supervisor mode.
 */
export type ExamSupervisionAccess = 'none' | 'pending' | 'unavailable' | 'disabled' | 'granted';

/**
 * Reads {@link ExamSupervisionAccess}. The flag only matters to a supervisor-only account, so a
 * teacher is `none` as soon as their permissions are known, flags loaded or not.
 *
 * @param flags `useSdk().Features.flags.value` — pass the ref's value so the reading stays reactive.
 * @param permissions the signed-in user's permissions, `undefined` until `me` resolves.
 * @param initialized `useSdk().initialized.value` — true once the SDK boot, flag load included, is over.
 */
export function examSupervisionAccess(
  flags: Record<string, boolean>,
  permissions: readonly string[] | null | undefined,
  initialized: boolean,
): ExamSupervisionAccess {
  // The normal shell loads `me` BEFORE it declares the boot over, so a boot over with no
  // permissions is a session that will never have a user — waiting would spin forever.
  if (!permissions) return initialized ? 'none' : 'pending';
  if (!isExamSupervisorOnly(permissions)) return 'none';
  if (Object.keys(flags).length === 0) return initialized ? 'unavailable' : 'pending';
  return flags[EXAM_SUPERVISION_FEATURE] === true ? 'granted' : 'disabled';
}

/**
 * Whether Présences may start the device's offline sync engine (#377) for this reader.
 *
 * Never for the exam supervisor, nor while it is not yet known whether the reader is one: the
 * outbox is the device's, not the user's, and their session would replay a teacher's queued cells,
 * each refused and marked failed. Otherwise once a reader is there — a signed-in user, or a token
 * session, which loads none: waiting for a user there would never start it. A session that has
 * just lost its user (expired, signed out) starts nothing either, for the same outbox.
 *
 * @param access {@link examSupervisionAccess} of the reader.
 * @param userId `useSdk().me.value?.user.id`.
 * @param tokenAuth `useSdk().isTokenAuth.value === true`.
 */
export function offlineSyncMayStart(
  access: ExamSupervisionAccess,
  userId: string | null | undefined,
  tokenAuth: boolean,
): boolean {
  return access === 'none' && (!!userId || tokenAuth);
}
